import { useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createConsumer } from '@rails/actioncable';
import { getConfig } from '../utils/config';
import { apiGet, apiPost, apiPatch } from '../utils/api';
import { getNotificationPrefs } from '../components/dashboard/views/NotificationSettings';
import toast from 'react-hot-toast';
import { getToken as getAuthToken } from '../stores/authStore';
import { useBadges } from './useBadges';

const NOTIFICATION_SOUND_URL = 'data:audio/wav;base64,UklGRlYAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YTIAAABkAKAA0AD4ABABGAEQAfgA0ACgAGQAKAD4/8j/oP+A/2j/YP9o/4D/oP/I//j/KAA=';

export function useNotifications() {
  const queryClient = useQueryClient();
  const { data: badges } = useBadges();
  const unreadCount = badges?.notifications?.unread_count || 0;

  const consumerRef = useRef(null);
  const audioRef = useRef(null);

  // Notifications list — fetched on demand (when the user opens the panel),
  // not on mount. The unread badge in the header uses unreadCount above.
  const { data: notificationsData, refetch: refetchNotifications } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await apiGet('/notifications');
      if (!response.ok) throw new Error(`Failed to fetch notifications (${response.status})`);
      return response.json();
    },
    enabled: false,
    staleTime: 60_000,
  });
  const notifications = notificationsData?.notifications || [];

  const fetchNotifications = useCallback(() => {
    refetchNotifications();
  }, [refetchNotifications]);

  // Mark one as read — optimistically updates local cache, server invalidates badges.
  const markReadMutation = useMutation({
    mutationFn: async (id) => {
      const response = await apiPatch(`/notifications/${id}/read`);
      if (!response.ok) throw new Error('Failed to mark notification as read');
      return response.json().catch(() => ({}));
    },
    onSuccess: (_data, id) => {
      queryClient.setQueryData(['notifications'], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          notifications: (prev.notifications || []).map(n =>
            n.id === id ? { ...n, read: true } : n
          ),
        };
      });
      queryClient.invalidateQueries({ queryKey: ['badges'] });
    },
  });
  const markRead = useCallback((id) => markReadMutation.mutate(id), [markReadMutation]);

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      const response = await apiPost('/notifications/mark_all_read');
      if (!response.ok) throw new Error('Failed to mark all notifications as read');
      return response.json().catch(() => ({}));
    },
    onSuccess: () => {
      queryClient.setQueryData(['notifications'], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          notifications: (prev.notifications || []).map(n => ({ ...n, read: true })),
        };
      });
      queryClient.invalidateQueries({ queryKey: ['badges'] });
    },
  });
  const markAllRead = useCallback(() => markAllReadMutation.mutate(), [markAllReadMutation]);

  // Play notification sound
  const playSound = useCallback(() => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(NOTIFICATION_SOUND_URL);
        audioRef.current.volume = 0.3;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch { /* silent */ }
  }, []);

  // Show toast notification — respects user preferences
  const showToast = useCallback((data) => {
    const prefs = getNotificationPrefs();
    const type = data.type;

    const toastEnabled =
      (type === 'task_assigned' && prefs.taskAssignedToast) ||
      (type === 'task_completed' && prefs.taskCompletedToast) ||
      (type === 'task_status_changed' && prefs.taskStatusChangedToast) ||
      (type === 'note_shared' && prefs.noteSharedToast) ||
      !['task_assigned', 'task_completed', 'task_status_changed', 'note_shared'].includes(type);

    if (!toastEnabled) return;

    const soundEnabled =
      (type === 'task_assigned' && prefs.taskAssignedSound) ||
      (type === 'note_shared' && prefs.noteSharedSound) ||
      false;

    if (soundEnabled) playSound();

    toast(data.title || 'New notification', {
      duration: 5000,
      style: { fontFamily: 'Rasa, serif' },
    });
  }, [playSound]);

  // Action Cable subscription for real-time push. The bundled /badges poll handles
  // the fallback path; this fires on push and triggers an immediate refetch.
  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;

    let consumer;
    try {
      const apiUrl = getConfig('API_URL') || 'https://localhost:3000/api/v1';
      const cableUrl = apiUrl
        .replace('/api/v1', '')
        .replace('https://', 'wss://')
        .replace('http://', 'ws://') + `/cable?token=${token}`;

      consumer = createConsumer(cableUrl);
      consumerRef.current = consumer;

      consumer.subscriptions.create(
        { channel: 'NotificationChannel' },
        {
          received(data) {
            if (data.type === 'new_notification') {
              queryClient.invalidateQueries({ queryKey: ['badges'] });
              queryClient.invalidateQueries({ queryKey: ['notifications'] });
              showToast({
                type: data.notification_type,
                title: data.title || 'New notification',
              });
            }
          },
        }
      );
    } catch (e) {
      console.error('Failed to connect to notification channel:', e);
    }

    return () => {
      if (consumer) {
        consumer.disconnect();
      }
      consumerRef.current = null;
    };
  }, [queryClient, showToast]);

  return {
    unreadCount,
    notifications,
    fetchNotifications,
    markRead,
    markAllRead,
  };
}
